import { ToolbarService } from '../services/appToolBarService';
import { IDocumentService } from '../../sharebase/services/documentService';
import { IFolderService } from '../../sharebase/services/folderService';
import { ISortContentFunction } from 'sharebase/filters/sortContent';
import {
	ShareBaseDocument,
	ShareBaseFolder,
	ShareBaseLibrary,
	pxItemTypeGuards as TypeGuard
} from 'phoenix/domain';

interface ChildShare {
	childShare:boolean;
	childShareCount:number;
}
export interface SharedContent {
	libraries: (ShareBaseLibrary & ChildShare)[],
	documents:(ShareBaseDocument & ChildShare)[],
	folders:(ShareBaseFolder & ChildShare)[]
}

/**
* @ngdoc service
* @name  sharebase-app.sbaSharedContentDataStore
* 
* @description 
* A datastore used to interact with the user's shared content
* 
**/
export function SharedContentDataStoreFactory(
	$q:ng.IQService,
	sbLibraryFactory,
	sortContent:ISortContentFunction,
	sbDocumentService:IDocumentService,
	sbFolderService:IFolderService,
	sbSharedContentService,
	sbaToolbarService:ToolbarService
) {
	let _content:SharedContent = null;

	return {
		/**
		* @ngdoc method
		* @name sbaSharedContentDataStore#getContent
		* @methodOf sharebase-app.sbaSharedContentDataStore
		* @kind function
		*
		* @description
		* Loads or refreshes the users shared content.
		*
		* @returns {Deferred} A promise that will resolve with the
		* users shared content.
		*/
		getContent,
		/**
		* @ngdoc property
		* @name sbaLibraryDataStore#content
		* @propertyOf sharebase-app.sbaLibraryDataStore
		* @kind property
		*
		* @description
		* The top level folders of the users shared content
		*
		* @returns {array} An object of the top level shared
		* content
		*/
		get content() { return _content; },
		/**
		 * @ngdoc method
		 * @name sbaSharedContentDataStore#sort
		 * @methodOf sharebase-app.sbaSharedContentDataStore
		 * @kind function
		 * 
		 * @description
		 * Sort shared content based on provided sort parameters
		 * 
		 * @param {Object} sortType Parameters that determine how to sort the data
		 */
		sort,
		renameItem
	};

	function getContent() {
		return sbSharedContentService
			.getContent()
			.then((content:SharedContent) => {
				// remove personal library
				content.libraries = content
					.libraries
					.filter(library => !library.IsPrivate)
					.map(sbLibraryFactory.create);
				

				if(content.folders.length > 0){
					// mark child folders of shared libraries
					content.folders = content.folders.map(folder => {
						let sharedLibrary = content.libraries.filter(lib => lib.LibraryId === folder.LibraryId);
						folder.childShare = sharedLibrary.length > 0;
						if(folder.childShare){	
							// increment library child count
							let lib = sharedLibrary[0];
							lib.childShareCount =  lib.childShareCount ? lib.childShareCount++ : 1;			
						}
						
						return folder;
					});
				}
				
				if(content.documents.length > 0){
					//mark documents that are children of shared libraries
					content.documents = content.documents.map(function(document){	
						let sharedLibrary = content.libraries.filter(function(lib){ return lib.LibraryId === document.LibraryId; });						
						document.childShare = sharedLibrary.length > 0;
						if(document.childShare){											
							//increment library child count
							let lib = sharedLibrary[0];
							lib.childShareCount =  lib.childShareCount ? lib.childShareCount++ : 1;	
						}

						return document;
					});
				}

				_content = content;
				sort(sbaToolbarService.sortType);

				return content;
			});
	}

	function sort(sortType) {
		_content.libraries = sortContent(_content.libraries, sortType.type, sortType.ascending) as (ShareBaseLibrary & ChildShare)[];
		_content.folders = sortContent(_content.folders, sortType.type, sortType.ascending) as (ShareBaseFolder & ChildShare)[];
		_content.documents = sortContent(_content.documents, sortType.type, sortType.ascending) as (ShareBaseDocument & ChildShare)[];
	}

	function renameItem(item:ShareBaseFolder|ShareBaseDocument, name:string){
		if(TypeGuard.isShareBaseDocument(item)){
			return renameDocument(item, name);
		}
		else if(TypeGuard.isShareBaseFolder(item)){
			return renameFolder(item, name);
		}
	}
	function renameFolder(folder:ShareBaseFolder, newName:string) {
		return sbFolderService
			.renameFolder(folder, newName)
			.then(renamedFolder => {
				_content.folders = _content.folders.map(function(folder) {
					if (folder.FolderId === renamedFolder.FolderId) {
						folder.FolderName = renamedFolder.FolderName;
					}
						return folder;
				});

				return renamedFolder;
			});
	}

	function renameDocument(document:ShareBaseDocument, newName:string) {
		return sbDocumentService
			.renameDocument(document, newName)
			.then(renamedDocument => {
				_content.documents = _content.documents.map(function(document) {
					if (document.DocumentId === renamedDocument.DocumentId) {
						document.DocumentName = renamedDocument.DocumentName;
					}
					return document;
				});

				return renamedDocument;
			});
	}
}

SharedContentDataStoreFactory.$inject = [
	'$q',	
	'sbLibraryFactory',
	'sbSortContentFilter',
	'sbDocumentService',
	'sbFolderService',
	'sbSharedContentService',
	'sbaToolbarService'
];


// WEBPACK FOOTER //
// ./src/sharebase-app/dataStores/sharedContentDataStore.ts